import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { FiMenu, FiX, FiArrowRight, FiChevronRight } from "react-icons/fi";
import "./Navbar.css";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/treatments", label: "Treatments" },
  { to: "/doctors", label: "Doctors" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  // Handle scroll effect
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const isTransparent = isHome && !scrolled && !isMenuOpen;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <header 
        className={`navbar ${isTransparent ? "navbar--transparent" : "navbar--solid"}`}
        role="banner"
      >
        <div className="navbar__container">
          {/* Logo */}
          <Link to="/" className="navbar__logo" aria-label="Roots Multispeciality Dental Clinic">
            <img src="/assets/logo1.png" alt="Roots Dental Clinic" />
            <div className="navbar__logo-text">
              <span className="navbar__logo-title">ROOTS</span>
              <span className="navbar__logo-subtitle">Multispeciality Dental Clinic</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="navbar__desktop" aria-label="Main navigation">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => 
                  `navbar__link ${isActive ? "navbar__link--active" : ""}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link to="/appointment" className="btn-primary navbar__cta">
            Book Appointment <FiArrowRight />
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            className="navbar__toggle"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu ${isMenuOpen ? "mobile-menu--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="mobile-menu__content">
          <nav className="mobile-menu__nav" aria-label="Mobile navigation">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => 
                  `mobile-menu__link ${isActive ? "mobile-menu__link--active" : ""}`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
                <FiChevronRight className="mobile-menu__link-icon" />
              </NavLink>
            ))}
          </nav>
          
          <div className="mobile-menu__footer">
            <Link 
              to="/appointment" 
              className="btn-primary mobile-menu__cta"
              onClick={() => setIsMenuOpen(false)}
            >
              Book Appointment <FiArrowRight />
            </Link>
            
            <div className="mobile-menu__contact">
              <a href="tel:+1234567890" className="mobile-menu__phone">
                📞 +1 (234) 567-890
              </a>
              <a href="mailto:info@rootsdental.com" className="mobile-menu__email">
                ✉️ info@rootsdental.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay backdrop */}
      <div 
        className={`mobile-overlay ${isMenuOpen ? "mobile-overlay--visible" : ""}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
    </>
  );
}